import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import request from './request';

export type CampaignMappingSource = {
  id: string;
  campaign_name: string;
  source_campaign_id?: string;
  source_type: string;
  source: string;
  table_name?: string;
  processed_table_name?: string;
  created_at?: string;
  updated_at?: string;
};

export type CampaignMapping = {
  campaign_id: number;
  campaign_name: string;
  original_campaign_name: string;
  created_at: string;
  updated_at: string;
  sources: CampaignMappingSource[];
};

// Query retry and expiration settings (same as queries.ts)
const QUERY_RETRY = false;
const APP_QUERY_EXPIRATION = 60 * 60 * 1000;
const DATA_QUERY_EXPIRATION = 12 * 60 * 60 * 1000;

// Query Hooks
export function useCampaignMappingsQuery(customer: string) {
  const queryId = useMemo(() => {
    return ['useCampaignMappingsQuery', customer];
  }, [customer]);

  return useQuery(
    queryId,
    () => {
      return request
        .get<{ data: CampaignMapping[] }>('/data/campaign-mapping/campaigns', {
          params: { customer },
        })
        .then((response) => {
          return response.data.data;
        });
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
      select: (data) => [...data].sort((a, b) => a.campaign_id - b.campaign_id),
      enabled: Boolean(customer),
    },
  );
}

export function fetchCampaignSources(
  customer: string,
  source: string,
  signal?: AbortSignal,
) {
  return request
    .get<{
      data: CampaignMappingSource[];
    }>(`/data/campaign-mapping/campaigns-by-source`, {
      params: { customer, source },
      signal,
    })
    .then((response) => {
      return response.data.data ?? [];
    });
}

export function useCampaignMappingsColumnsQuery(customer: string) {
  const queryId = useMemo(() => {
    return ['useCampaignMappingsColumnsQuery', customer];
  }, [customer]);

  return useQuery(
    queryId,
    () => {
      return request
        .get<{
          data: string[];
        }>(`/data/campaign-mapping/campaigns-sources`, { params: { customer } })
        .then((response) => {
          return response.data.data ?? [];
        });
    },
    { retry: QUERY_RETRY, staleTime: DATA_QUERY_EXPIRATION },
  );
}

export function useUpdateCampaignMappingSourceMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      customer,
      campaignMappingId,
      sourceId,
    }: {
      customer: string;
      campaignMappingId: number | null; // null to deselect (remove from all)
      sourceId: string;
    }) => {
      return request.put(
        '/data/campaign-mapping/update-source',
        { campaignMappingId, sourceId },
        { params: { customer } },
      );
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          'useCampaignMappingsQuery',
          variables.customer,
        ]);
      },
    },
  );
}

export function useUpdateCampaignMappingNameMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      customer,
      campaignMappingId,
      name,
    }: {
      customer: string;
      campaignMappingId: number;
      name: string;
    }) => {
      return request.put(
        '/data/campaign-mapping/update-name',
        { campaignMappingId, newName: name },
        { params: { customer } },
      );
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          'useCampaignMappingsQuery',
          variables.customer,
        ]);
      },
    },
  );
}
